import React, { useEffect } from 'react';

// Simple analytics tracker for user behavior
class AnalyticsTracker {
  private events: Array<{
    event: string;
    properties: Record<string, any>;
    timestamp: Date;
  }> = [];

  track(event: string, properties: Record<string, any> = {}) {
    const eventData = {
      event,
      properties: {
        ...properties,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      },
      timestamp: new Date()
    };

    this.events.push(eventData);
    
    // Store in localStorage for premium users
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user?.isPremium) {
      this.persistEvent(eventData);
    }

    // Events reach GA4 when REACT_APP_GA_MEASUREMENT_ID is set (see index.tsx).
    if (process.env.NODE_ENV !== 'production') {
      console.log('Analytics:', eventData);
    }
  }

  private persistEvent(eventData: any) {
    const stored = JSON.parse(localStorage.getItem('analytics_events') || '[]');
    stored.push(eventData);
    
    // Keep only last 1000 events
    if (stored.length > 1000) {
      stored.splice(0, stored.length - 1000);
    }
    
    localStorage.setItem('analytics_events', JSON.stringify(stored));
  }

  getEvents(): any[] {
    const stored = JSON.parse(localStorage.getItem('analytics_events') || '[]');
    return [...this.events, ...stored];
  }

  // Page view tracking
  trackPageView(page: string) {
    this.track('page_view', { page });
  }

  // Business card events
  trackCardCreated(cardType: string = 'business') {
    this.track('card_created', { cardType });
  }

  trackCardViewed(cardId: string, cardName: string) {
    this.track('card_viewed', { cardId, cardName });
  }

  trackCardShared(cardId: string, method: 'qr' | 'link' | 'export' | 'share_api' | 'download_fallback') {
    this.track('card_shared', { cardId, method });
  }

  trackCardExported(format: string, count: number) {
    this.track('card_exported', { format, count });
  }

  // Exchange events
  trackExchangeInitiated(method: 'qr_scan' | 'manual') {
    this.track('exchange_initiated', { method });
  }

  trackExchangeCompleted(success: boolean, method: string) {
    this.track('exchange_completed', { success, method });
  }

  // Feature usage
  trackFeatureUsed(feature: string, context?: string | Record<string, any>) {
    this.track('feature_used', { feature, context });
  }

  // Subscription events
  trackSubscriptionEvent(action: 'viewed' | 'started_checkout' | 'completed' | 'cancelled') {
    this.track('subscription_' + action, {});
  }

  // User journey tracking
  trackUserJourney(step: string, completed: boolean = true) {
    this.track('user_journey', { step, completed });
  }
}

// Singleton instance
export const analytics = new AnalyticsTracker();

// React component for tracking page views
interface AnalyticsProviderProps {
  children: React.ReactNode;
  page?: string;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ 
  children, 
  page 
}) => {
  useEffect(() => {
    if (page) {
      analytics.trackPageView(page);
    }
  }, [page]);

  return <>{children}</>;
};

// Hook for easy analytics access
export const useAnalytics = () => {
  return analytics;
};

// Higher-order component for automatic event tracking
export function withAnalytics<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  eventName: string
) {
  return function AnalyticsWrapper(props: P) {
    useEffect(() => {
      analytics.track(eventName, { component: WrappedComponent.name });
    }, []);

    return <WrappedComponent {...props} />;
  };
}

// Google Analytics 4 integration (optional)
export const initializeGA4 = (measurementId: string) => {
  if (typeof window === 'undefined') return;

  // Load Google Analytics script
  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script1);

  const script2 = document.createElement('script');
  script2.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${measurementId}');
  `;
  document.head.appendChild(script2);

  // Extend analytics to also send to GA4
  const originalTrack = analytics.track.bind(analytics);
  analytics.track = (event: string, properties: Record<string, any> = {}) => {
    originalTrack(event, properties);
    
    if (window.gtag) {
      window.gtag('event', event, {
        event_category: properties.category || 'engagement',
        event_label: properties.label,
        value: properties.value,
        custom_parameters: properties
      });
    }
  };
};

// Mixpanel integration (optional)
export const initializeMixpanel = (projectToken: string) => {
  if (typeof window === 'undefined') return;

  // Simple Mixpanel integration
  const script = document.createElement('script');
  script.innerHTML = `
    (function(c,a){if(!a.__SV){var b=window;try{var d,m,j,k=b.location,f=k.hash;d=function(a,b){return(m=a.match(RegExp(b+"=([^&]*)")))?(m[1]):null};f&&d(f,"state")&&(j=JSON.parse(decodeURIComponent(d(f,"state"))),"mpeditor"===j.action&&(b.sessionStorage.setItem("_mpcehash",f),history.replaceState(j.desiredHash||"",c.title,k.pathname+k.search)))}catch(n){}var l,h;window.mixpanel=a;a._i=[];a.init=function(b,d,g){function c(b,i){var a=i.split(".");2==a.length&&(b=b[a[0]],i=a[1]);b[i]=function(){b.push([i].concat(Array.prototype.slice.call(arguments,0)))}}var e=a;"undefined"!==typeof g?e=a[g]=[]:g="mixpanel";e.people=e.people||[];e.toString=function(b){var a="mixpanel";"mixpanel"!==g&&(a+="."+g);b||(a+=" (stub)");return a};e.people.toString=function(){return e.toString(1)+".people (stub)"};l="disable time_event track track_pageview track_links track_forms register register_once alias unregister identify name_tag set_config reset people.set people.set_once people.increment people.append people.union people.track_charge people.clear_charges people.delete_user".split(" ");for(h=0;h<l.length;h++)c(e,l[h]);a._i.push([b,d,g])};a.__SV=1.2;b=c.createElement("script");b.type="text/javascript";b.async=!0;b.src="undefined"!==typeof MIXPANEL_CUSTOM_LIB_URL?MIXPANEL_CUSTOM_LIB_URL:"file:"===c.location.protocol&&"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js".match(/^\\/\\//)?"https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js":"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";d=c.getElementsByTagName("script")[0];d.parentNode.insertBefore(b,d)}})(document,window.mixpanel||[]);
    mixpanel.init("${projectToken}");
  `;
  document.head.appendChild(script);

  // Extend analytics to also send to Mixpanel
  const originalTrack = analytics.track.bind(analytics);
  analytics.track = (event: string, properties: Record<string, any> = {}) => {
    originalTrack(event, properties);
    
    if (window.mixpanel) {
      window.mixpanel.track(event, properties);
    }
  };
};

declare global {
  interface Window {
    gtag: any;
    mixpanel: any;
  }
}

export default AnalyticsProvider;